import { Divider, Flex, Layout, Menu, Radio } from "antd";
import { Icon, Link, Space, Title } from "components/common";
import Logo from "components/common/Logo/Logo";
import { UserMenu } from "components/custom";
import { configs } from "configs";
import Notifications from "features/shared/components/notifications/notifications";
import { getAppLanguage } from "helpers/getAppLanguage";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { setSelectedMenuItem } from "store/appSlice";
import developerMenuConfig from "../../../apps/developer/menu-items";
import styles from "./developerShell.module.less";

const { Header, Content } = Layout;

const checkIfPathMatches = (modulePath, pathname) => {
  const stripped = pathname.replace(/^\/(en|ar)/, "").replace(/^\/developer/, "");
  if (Array.isArray(modulePath)) {
    return modulePath.some((p) => stripped.startsWith(p?.split("?")[0]));
  }
  return stripped.startsWith(modulePath?.split("?")[0]);
};

const DeveloperShell = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const privileges = useSelector((state) => state.app.privileges);
  const selectedMenuItem = useSelector((state) => state.app.selectedMenuItem);
  const currentLanguage = getAppLanguage();
  const [locale, setLocale] = useState(currentLanguage?.key || "en");
  const [langLoading, setLangLoading] = useState(false);

  const menuList = useMemo(
    () => developerMenuConfig.menuItems(privileges, false, t),
    [privileges]
  );

  useEffect(() => {
    const active = menuList.find((item) =>
      checkIfPathMatches(item.path, location.pathname)
    );
    if (active && active?.key !== selectedMenuItem?.key) {
      dispatch(setSelectedMenuItem(active));
    }
  }, [location.pathname, menuList, dispatch]);

  useEffect(() => {
    const lang = getAppLanguage();
    if (lang?.key !== locale) {
      setLocale(lang?.key || "en");
    }
  }, [location.pathname]);

  const onClickMenuItem = (e) => {
    const item = menuList.find((it) => it.key === e.key);
    if (item) dispatch(setSelectedMenuItem(item));
  };

  const onChangeLanguage = (e) => {
    const newLang = e.target.value;
    if (newLang === locale) return;
    setLangLoading(true);
    const lang = configs.languages.find((l) => l.key === newLang);
    if (lang) {
      const currentPath = location.pathname;
      const pathWithoutLang = currentPath.replace(/^\/(en|ar)/, "") || "/";
      const newUrl =
        window.location.origin +
        "/" +
        lang.key +
        pathWithoutLang +
        location.search;
      window.location.href = newUrl;
    } else {
      setLangLoading(false);
    }
  };

  return (
    <Layout className={styles.layout}>
      <Header className={styles.header}>
        <Flex
          align="center"
          justify="space-between"
          className={styles.headerInner}
        >
          <Flex align="center" gap={16}>
            <Link
              to="/inventory"
              style={{ display: "flex", alignItems: "center" }}
            >
              <Logo width="137" />
            </Link>
            <Divider type="vertical" style={{ height: 24, marginInline: 16 }} />
            <Title className="m-0" level={3}>
              {t("Developer Portal")}
            </Title>
          </Flex>
          <Flex style={{ flex: 1 }} gap={48} justify="end">
            <Menu
              style={{ justifyContent: "end", border: "none", flex: 1 }}
              mode="horizontal"
              onClick={onClickMenuItem}
              selectedKeys={[selectedMenuItem?.key]}
              items={menuList}
            />
            <Space className={styles.navbarIcons} size={20}>
              {configs.languages.length > 1 && (
                <Radio.Group
                  className={styles.languageSwitcher}
                  buttonStyle="solid"
                  value={locale}
                  size="small"
                  onChange={onChangeLanguage}
                  disabled={langLoading}
                >
                  {configs.languages?.map((e) => (
                    <Radio.Button key={e.key} value={e.key}>
                      <span>
                        <Icon icon="MdGTranslate" size={14} />
                      </span>
                      {e.label}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              )}
              <Notifications app="developer"/>
              <UserMenu />
            </Space>
          </Flex>
        </Flex>
      </Header>
      <Content className={styles.content}>{children}</Content>
    </Layout>
  );
};

export default DeveloperShell;
